// CustomerSelector.tsx
// Customer Selection Component for Booking Flow

import React, { useState, useEffect } from 'react';
import { adminService } from '../../services/adminService';
import { CustomerDialog, Customer } from '../common/CustomerDialog';
import { useAuth } from '../../contexts/AuthContext';
import '../../styles/CustomerSelector.css';

interface CustomerSelectorProps {
  onCustomerSelect: (customer: Customer) => void;
  selectedCustomer: Customer | null;
}

export const CustomerSelector: React.FC<CustomerSelectorProps> = ({
  onCustomerSelect,
  selectedCustomer
}) => {
  // State
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterVip, setFilterVip] = useState(false);
  const [showNewCustomerDialog, setShowNewCustomerDialog] = useState(false);

  // Get user info from auth context
  const { user } = useAuth();
  const isAdmin = user?.role === 'Admin' || user?.role === 'Staff';

  // Fetch customers on mount
  useEffect(() => {
    if (isAdmin) {
      // For admins, fetch customer list
      fetchCustomers();
    } else if (user) {
      // For regular customers, auto-select themselves
      const currentUserAsCustomer: Customer = {
        id: user.id,
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email,
        phone: (user as any).phone || '',
        companyName: user.companyName || '',
        isVip: false,
        addresses: ((user as any).addresses || []).map((a: any) => ({
          street: a.street || '',
          city: a.city || '',
          postalCode: a.postalCode || '',
          country: a.country || 'Deutschland',
          type: a.addressType || 'billing',
          isDefault: a.isDefault || false
        }))
      };
      onCustomerSelect(currentUserAsCustomer);
    }
  }, [isAdmin, user]);

  const fetchCustomers = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await adminService.getCustomers({
        page: 1,
        pageSize: 100
      });

      // Transform API response to Customer format
      const responseData: any = response;
      const customersData = responseData.data?.Customers || responseData.data?.customers || response.customers || [];

      const transformedCustomers: Customer[] = customersData.map((c: any) => ({
        id: (c.Id || c.id) || 0,
        firstName: c.FirstName || c.firstName || '',
        lastName: c.LastName || c.lastName || '',
        email: c.Email || c.email || '',
        phone: c.Phone || c.phone || '',
        companyName: c.CompanyName || c.companyName || '',
        isVip: c.IsVip || c.isVip || false,
        addresses: (c.Addresses || c.addresses || []).map((a: any) => ({
          street: a.Street || a.street || '',
          city: a.City || a.city || '',
          postalCode: a.PostalCode || a.postalCode || '',
          country: a.Country || a.country || 'Deutschland',
          type: (a.AddressType || a.addressType || 'billing') as any,
          isDefault: a.IsDefault || a.isDefault || false
        }))
      }));

      setCustomers(transformedCustomers);
    } catch (error: any) {
      console.error('Error fetching customers:', error);
      setError('Fehler beim Laden der Kundenliste. Bitte versuchen Sie es erneut.');
    } finally {
      setLoading(false);
    }
  };

  // Filter customers based on search and VIP filter
  const filteredCustomers = customers.filter(customer => {
    const matchesSearch =
      searchTerm === '' ||
      customer.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customer.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customer.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (customer.phone && customer.phone.includes(searchTerm)) ||
      (customer.companyName && customer.companyName.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesVip = !filterVip || customer.isVip;

    return matchesSearch && matchesVip;
  });

  // Handle new customer saved
  const handleNewCustomerSaved = (newCustomer: Customer) => {
    setCustomers([newCustomer, ...customers]);
    onCustomerSelect(newCustomer);
    setShowNewCustomerDialog(false);
  };

  return (
    <div className="customer-selector">
      {/* For non-admin users, show current user info */}
      {!isAdmin && selectedCustomer ? (
        <div className="current-user-info">
          <h3>Buchung für:</h3>
          <div className="customer-item selected">
            <div className="customer-info">
              <div className="customer-header">
                <h4>
                  {selectedCustomer.firstName} {selectedCustomer.lastName}
                </h4>
              </div>
              <p className="customer-email">
                <span className="icon">📧</span>
                {selectedCustomer.email}
              </p>
              {selectedCustomer.phone && (
                <p className="customer-phone">
                  <span className="icon">📞</span>
                  {selectedCustomer.phone}
                </p>
              )}
              {selectedCustomer.companyName && (
                <p className="customer-company">
                  <span className="icon">🏢</span>
                  <strong>{selectedCustomer.companyName}</strong>
                </p>
              )}
            </div>
          </div>
          <p className="info-text">Sie buchen diese Floßfahrt für sich selbst.</p>
        </div>
      ) : isAdmin ? (
        <>
          {/* Search & Filter Bar (Admin only) */}
          <div className="search-filter-bar">
            <div className="search-box">
              <input
                type="text"
                placeholder="Suchen nach Name, Email, Telefon, Firma..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
              <span className="search-icon">🔍</span>
            </div>

            <label className="vip-filter">
              <input
                type="checkbox"
                checked={filterVip}
                onChange={(e) => setFilterVip(e.target.checked)}
              />
              <span>Nur VIP-Kunden</span>
            </label>

            <button
              onClick={() => setShowNewCustomerDialog(true)}
              className="btn-new-customer"
            >
              <span className="btn-icon">➕</span>
              Neuer Kunde
            </button>
          </div>

          {/* Customer List (Admin only) */}
          {loading ? (
        <div className="loading-state">
          <div className="spinner">⏳</div>
        </div>
      ) : error ? (
        <div className="error-state">
          <div className="error-icon">⚠️</div>
          <p>{error}</p>
          <button onClick={fetchCustomers} className="btn-retry">
            Erneut versuchen
          </button>
        </div>
      ) : (
        <div className="customer-list">
          {filteredCustomers.length === 0 ? (
            <div className="no-results">
              <div className="no-results-icon">🔍</div>
              <p>Keine Kunden gefunden</p>
              <button
                onClick={() => setShowNewCustomerDialog(true)}
                className="btn-new-customer-large"
              >
                <span className="btn-icon">➕</span>
                Neuen Kunde anlegen
              </button>
            </div>
          ) : (
            filteredCustomers.map((customer) => (
              <div
                key={customer.id}
                className={`customer-item ${
                  selectedCustomer?.id === customer.id ? 'selected' : ''
                }`}
                onClick={() => onCustomerSelect(customer)}
              >
                <div className="customer-info">
                  <div className="customer-header">
                    <h4>
                      {customer.firstName} {customer.lastName}
                    </h4>
                    {customer.isVip && <span className="vip-badge">VIP</span>}
                  </div>
                  <p className="customer-email">
                    <span className="icon">📧</span>
                    {customer.email}
                  </p>
                  {customer.phone && (
                    <p className="customer-phone">
                      <span className="icon">📞</span>
                      {customer.phone}
                    </p>
                  )}
                  {customer.companyName && (
                    <p className="customer-company">
                      <span className="icon">🏢</span>
                      <strong>{customer.companyName}</strong>
                    </p>
                  )}
                </div>
                {selectedCustomer?.id === customer.id && (
                  <div className="selected-indicator">✓</div>
                )}
              </div>
            ))
          )}
        </div>
      )}

          {/* New Customer Dialog */}
          {showNewCustomerDialog && (
            <CustomerDialog
              onClose={() => setShowNewCustomerDialog(false)}
              onSave={handleNewCustomerSaved}
              customer={null}
            />
          )}
        </>
      ) : null}
    </div>
  );
};
